// =============================================================================
// conversations/hooks/useStartConversationOnChannel.ts — Mutation para
// `POST /api/conversations/iniciar` (F43-S10, contrato lido em
// apps/api/src/modules/conversations/start-channel.schema.ts).
//
// Endpoint unificado multi-canal (Meta oficial ou WhatsApp pessoal do
// vendedor) — o vendedor escolhe a caixa na tela de F43-S11.
//
// ⚠️ Idempotency-Key — mesma exigência de segurança de
// hooks/crm/useStartConversation.ts (F41-S02, revisão do PR #605): gerada
// ALEATORIAMENTE a cada tentativa, nunca derivada de leadId/channelId.
// `idempotency_keys` é uma tabela GLOBAL sem escopo de endpoint.
// =============================================================================

import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '../../../lib/api';

// ---------------------------------------------------------------------------
// Tipos do contrato (espelham start-channel.schema.ts).
// ---------------------------------------------------------------------------

export interface StartConversationOnChannelBody {
  leadId: string;
  channelId: string;
  /** Texto livre. Exatamente um entre `corpo` e `templateName`. */
  corpo?: string;
  templateName?: string;
  languageCode?: string;
  variables?: string[];
}

export interface StartConversationOnChannelResponse {
  conversationId: string;
  messageId: string;
  conversationCreated: boolean;
  status: 'sent' | 'queued';
  channelId: string;
  provider: 'meta_whatsapp' | 'waha';
}

// ---------------------------------------------------------------------------
// Classificação de erro — mesmo padrão de hooks/crm/useStartConversation.ts.
// ---------------------------------------------------------------------------

export type StartConversationOnChannelErrorKind =
  | 'not_found'
  | 'idempotency_conflict'
  | 'send_failed'
  | 'rate_limited'
  | 'generic';

export interface StartConversationOnChannelError {
  kind: StartConversationOnChannelErrorKind;
  message: string;
}

export function classifyStartOnChannelError(err: unknown): StartConversationOnChannelError {
  if (err instanceof ApiError) {
    if (err.status === 404) {
      return {
        kind: 'not_found',
        message: 'Este lead ou esta caixa não foi encontrada — atualize a lista e tente de novo.',
      };
    }
    if (err.status === 409) {
      return {
        kind: 'idempotency_conflict',
        message:
          err.message ||
          'Já existe um envio em andamento para este contato. Aguarde alguns segundos e tente novamente.',
      };
    }
    if (err.status === 429) {
      return {
        kind: 'rate_limited',
        message: 'Muitas conversas iniciadas em pouco tempo. Aguarde um minuto e tente de novo.',
      };
    }
    if (err.status === 502) {
      return { kind: 'send_failed', message: err.message };
    }
    return { kind: 'generic', message: err.message || 'Erro ao iniciar a conversa.' };
  }

  return {
    kind: 'generic',
    message:
      err instanceof Error && err.message ? err.message : 'Erro inesperado ao iniciar a conversa.',
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseStartConversationOnChannelOptions {
  onSuccess?: (response: StartConversationOnChannelResponse) => void;
  onError?: (error: StartConversationOnChannelError) => void;
}

export function useStartConversationOnChannel(opts: UseStartConversationOnChannelOptions = {}): {
  startConversation: (body: StartConversationOnChannelBody) => void;
  isPending: boolean;
} {
  const mutation = useMutation({
    mutationFn: (
      body: StartConversationOnChannelBody,
    ): Promise<StartConversationOnChannelResponse> => {
      // Chave aleatória por tentativa — nunca reaproveitar entre cliques.
      const idempotencyKey = `conversation.start-channel:${crypto.randomUUID()}`;
      return api.post<StartConversationOnChannelResponse>('/api/conversations/iniciar', body, {
        headers: { 'Idempotency-Key': idempotencyKey },
      });
    },
    onSuccess: (response) => opts.onSuccess?.(response),
    onError: (err: unknown) => opts.onError?.(classifyStartOnChannelError(err)),
  });

  return {
    startConversation: (body: StartConversationOnChannelBody) => mutation.mutate(body),
    isPending: mutation.isPending,
  };
}
